import { Component, computed, effect, inject, signal } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";

import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { ImportStoreService } from "@bitwarden/web-vault/app/services/store/import/import.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { SourceTransactionsView } from "@bitwarden/web-vault/app/models/view/source-transactions/source-transactions.view";
import { SourceBalancesView } from "@bitwarden/web-vault/app/models/view/source-balances/source-balances.view";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { VaultExplorerService } from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import { VaultCodeEnum } from "@bitwarden/web-vault/app/services/dali/type/dali.type";

@Component({
  selector: "app-manage-source-explorer",
  templateUrl: "./manage-source-explorer.component.html",
})
export class ManageSourceExplorerComponent {
  private userStore = inject(UserStoreService);
  private importStore = inject(ImportStoreService);
  private transactionStore = inject(TransactionStoreService);
  private i18nService = inject(I18nService);
  vaultExplorer = inject(VaultExplorerService);

  constructor() {
    effect(() => {
      const rows = this.filteredSourceTransactions();
      const ids = new Set(rows.map((r) => r.id));
      const exp = this.expandedSourceRowId();
      if (exp && !ids.has(exp)) {
        this.expandedSourceRowId.set(null);
      }
      const jsonExp = this.jsonExpandedSourceRowId();
      if (jsonExp && !ids.has(jsonExp)) {
        this.jsonExpandedSourceRowId.set(null);
      }
    });
    this.vaultExplorer.loadVaultTypes();
  }

  /** Selected vault account id, or null. */
  readonly selectedAccountId = signal<string | null>(null);

  /** Selected source-balance row id for the balance dropdown / JSON panel; null = placeholder only. */
  readonly selectedBalanceId = signal<string | null>(null);

  /** Inclusive date filter; both null = no date filter (all rows for account). */
  readonly dateFilterStart = signal<Date | null>(null);
  readonly dateFilterEnd = signal<Date | null>(null);

  /** Substring filter on `description`; empty / whitespace-only = no filter. Comparison uses trim + lowercase on both sides. */
  readonly descriptionFilter = signal("");

  /** Source row id whose linked vault transaction detail is expanded; only one at a time. */
  readonly expandedSourceRowId = signal<string | null>(null);

  /** Source row id whose linked `TransactionView` JSON panel is shown; only one at a time. */
  readonly jsonExpandedSourceRowId = signal<string | null>(null);

  /** When true, show resolved category JSON above the transaction JSON in the panel (default on). */
  readonly jsonPanelShowCategories = signal(true);

  /** Recreates `app-date-picker` instances when filters clear (component does not sync `dateText` on change). */
  readonly datePickerNonce = signal(0);

  readonly accounts = computed(() => this.userStore.accounts.accountViews() ?? []);

  /**
   * First entry is the "no account" label (same as mat-select's null option); then sorted names.
   */
  readonly accountDropdownOptions = computed(() => {
    const ph = this.i18nService.t("sourceExplorerSelectAccountPlaceholder");
    const names = (this.accounts() ?? []).map((a) => a.name).sort((a, b) => a.localeCompare(b));
    return [ph, ...names];
  });

  readonly selectedAccountName = computed(() => this.selectedAccount()?.name ?? "");

  /**
   * Source balance rows for the current account, newest snapshot first (by `balanceSnapshotAt`).
   */
  readonly sortedBalancesForSelectedAccount = computed((): SourceBalancesView[] => {
    const accountId = this.selectedAccountId();
    if (!accountId) {
      return [];
    }
    const all = this.importStore.sourceBalance.sourceBalanceViews();
    if (!all?.length) {
      return [];
    }
    return all
      .filter((b) => b.accountId === accountId)
      .sort((a, b) => {
        const ta = Date.parse(a.balanceSnapshotAt) || 0;
        const tb = Date.parse(b.balanceSnapshotAt) || 0;
        return tb - ta;
      });
  });

  readonly balanceDropdownOptions = computed((): string[] => {
    const ph = this.i18nService.t("sourceExplorerBalancesPlaceholder");
    const sorted = this.sortedBalancesForSelectedAccount();
    if (sorted.length === 0) {
      return [ph];
    }
    return [ph, ...sorted.map((b) => this.balanceOptionLabel(b))];
  });

  readonly balanceSelectEnabled = computed(
    () => this.selectedAccountId() !== null && this.sortedBalancesForSelectedAccount().length > 0,
  );

  readonly selectedBalanceLabel = computed((): string => {
    const ph = this.i18nService.t("sourceExplorerBalancesPlaceholder");
    const id = this.selectedBalanceId();
    if (!id) {
      return ph;
    }
    const b = this.sortedBalancesForSelectedAccount().find((x) => x.id === id);
    return b ? this.balanceOptionLabel(b) : ph;
  });

  /** Pretty-printed JSON from the selected `SourceBalancesView.raw`, or a short empty message. */
  readonly selectedBalancesRawJson = computed((): string => {
    const id = this.selectedBalanceId();
    if (!id) {
      return this.i18nService.t("sourceExplorerBalanceJsonEmpty");
    }
    const b = this.sortedBalancesForSelectedAccount().find((x) => x.id === id);
    const raw = b?.raw?.trim();
    if (!raw) {
      return this.i18nService.t("sourceExplorerBalanceJsonEmpty");
    }
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return `/* ${this.i18nService.t("sourceExplorerBalanceJsonInvalid")} */\n${raw}`;
    }
  });

  /** Bounds for calendar filtering between from/to pickers. */
  readonly pickerDateRange = computed(() => {
    const start = this.dateFilterStart() ?? new Date(0);
    const end = this.dateFilterEnd() ?? new Date(8_640_000_000_000_000);
    return new DateRange(start, end);
  });

  readonly dateFilterStartDisplay = computed(() => this.formatYmdOrEmpty(this.dateFilterStart()));

  readonly dateFilterEndDisplay = computed(() => this.formatYmdOrEmpty(this.dateFilterEnd()));

  readonly selectedAccount = computed(() => {
    const id = this.selectedAccountId();
    if (!id) {
      return null;
    }
    return this.accounts().find((a) => a.id === id) ?? null;
  });

  readonly filteredSourceTransactions = computed((): SourceTransactionsView[] => {
    const accountId = this.selectedAccountId();
    if (!accountId) {
      return [];
    }
    const all = this.importStore.sourceTransaction.sourceTransactionViews();
    if (!all?.length) {
      return [];
    }
    const start = this.dateFilterStart();
    const end = this.dateFilterEnd();
    const descNeedle = this.normalizedDescriptionNeedle(this.descriptionFilter());
    return all
      .filter((st) => st.accountId === accountId)
      .filter((st) => this.sourceRowInDateRange(st, start, end))
      .filter((st) => this.sourceRowMatchesDescription(st, descNeedle))
      .sort((a, b) => b.dateCreated.getTime() - a.dateCreated.getTime());
  });

  readonly totals = computed(() => {
    const rows = this.filteredSourceTransactions();
    let totalIn = 0;
    let totalOut = 0;
    for (const row of rows) {
      const amt = Math.abs(parseFloat(String(row.quantity))) || 0;
      if (row.direction === TransactionDirection.In) {
        totalIn += amt;
      } else if (row.direction === TransactionDirection.Out) {
        totalOut += amt;
      }
    }
    const net = totalIn - totalOut;
    return {
      count: rows.length,
      totalIn,
      totalOut,
      net,
    };
  });

  onAccountChange(value: string | null): void {
    this.selectedAccountId.set(value);
    this.dateFilterStart.set(null);
    this.dateFilterEnd.set(null);
    this.descriptionFilter.set("");
    this.expandedSourceRowId.set(null);
    this.jsonExpandedSourceRowId.set(null);
    this.datePickerNonce.update((n) => n + 1);
    this.applyDefaultBalanceSelectionForAccount(value);
  }

  /** After account changes: clear balance, or select the newest snapshot for that account. */
  private applyDefaultBalanceSelectionForAccount(accountId: string | null): void {
    if (!accountId) {
      this.selectedBalanceId.set(null);
      return;
    }
    const all = this.importStore.sourceBalance.sourceBalanceViews();
    if (!all?.length) {
      this.selectedBalanceId.set(null);
      return;
    }
    const sorted = all
      .filter((b) => b.accountId === accountId)
      .sort((a, b) => {
        const ta = Date.parse(a.balanceSnapshotAt) || 0;
        const tb = Date.parse(b.balanceSnapshotAt) || 0;
        return tb - ta;
      });
    this.selectedBalanceId.set(sorted[0]?.id ?? null);
  }

  onBalanceDropdownSelected(optionLabel: string): void {
    const ph = this.i18nService.t("sourceExplorerBalancesPlaceholder");
    if (optionLabel === ph) {
      this.selectedBalanceId.set(null);
      return;
    }
    const sorted = this.sortedBalancesForSelectedAccount();
    const found = sorted.find((b) => this.balanceOptionLabel(b) === optionLabel);
    this.selectedBalanceId.set(found?.id ?? null);
  }

  /** Stable label for dropdown rows (time + id prefix so options stay unique). */
  balanceOptionLabel(b: SourceBalancesView): string {
    const dt = b.balanceSnapshotAt ? new Date(b.balanceSnapshotAt).toLocaleString() : "—";
    return `${dt} · ${b.id.slice(0, 8)}`;
  }

  onAccountDropdownSelected(name: string): void {
    const ph = this.i18nService.t("sourceExplorerSelectAccountPlaceholder");
    if (name === ph) {
      this.onAccountChange(null);
      return;
    }
    const acc = this.accounts().find((a) => a.name === name);
    this.onAccountChange(acc?.id ?? null);
  }

  clearDateFilter(): void {
    this.dateFilterStart.set(null);
    this.dateFilterEnd.set(null);
    this.expandedSourceRowId.set(null);
    this.jsonExpandedSourceRowId.set(null);
    this.datePickerNonce.update((n) => n + 1);
  }

  clearDescriptionFilter(): void {
    this.descriptionFilter.set("");
    this.expandedSourceRowId.set(null);
    this.jsonExpandedSourceRowId.set(null);
  }

  onDescriptionOutput(value: string | number | null | undefined): void {
    this.descriptionFilter.set(String(value ?? ""));
    this.expandedSourceRowId.set(null);
    this.jsonExpandedSourceRowId.set(null);
  }

  onDateFilterChange(dateStr: string, bound: "start" | "end"): void {
    const s = String(dateStr ?? "").trim();
    if (!s) {
      if (bound === "start") {
        this.dateFilterStart.set(null);
      } else {
        this.dateFilterEnd.set(null);
      }
    } else {
      const parts = s.split("-");
      if (parts.length !== 3) {
        return;
      }
      const y = Number(parts[0]);
      const mo = Number(parts[1]) - 1;
      const day = Number(parts[2]);
      const d = new Date(y, mo, day);
      if (bound === "start") {
        this.dateFilterStart.set(d);
      } else {
        this.dateFilterEnd.set(d);
      }
    }
    this.expandedSourceRowId.set(null);
    this.jsonExpandedSourceRowId.set(null);
  }

  private formatYmdOrEmpty(d: Date | null): string {
    if (!d || isNaN(d.getTime())) {
      return "";
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  /** Trim + lowercase for description filter needle; empty string means "no filter". */
  private normalizedDescriptionNeedle(raw: string): string {
    return String(raw ?? "")
      .trim()
      .toLowerCase();
  }

  /** Trim + lowercase transaction description for matching. */
  private normalizedRowDescription(row: SourceTransactionsView): string {
    return String(row.description ?? "")
      .trim()
      .toLowerCase();
  }

  private sourceRowMatchesDescription(row: SourceTransactionsView, needle: string): boolean {
    if (!needle) {
      return true;
    }
    return this.normalizedRowDescription(row).includes(needle);
  }

  private sourceRowInDateRange(
    row: SourceTransactionsView,
    start: Date | null,
    end: Date | null,
  ): boolean {
    if (!start && !end) {
      return true;
    }
    const d = new Date(row.transactionDate);
    if (isNaN(d.getTime())) {
      return false;
    }
    if (start) {
      const s = new Date(start);
      s.setHours(0, 0, 0, 0);
      if (d < s) {
        return false;
      }
    }
    if (end) {
      const e = new Date(end);
      e.setHours(23, 59, 59, 999);
      if (d > e) {
        return false;
      }
    }
    return true;
  }

  findLinkedTransaction(sourceId: string | undefined): TransactionView | undefined {
    if (!sourceId) {
      return undefined;
    }
    const txs = this.transactionStore.enrichTransaction.collection();
    return txs?.find((t) => t.id === sourceId);
  }

  hasLinkedTransaction(row: SourceTransactionsView): boolean {
    return !!this.findLinkedTransaction(row.sourceId);
  }

  toggleVaultTransactionDetail(row: SourceTransactionsView): void {
    if (!this.findLinkedTransaction(row.sourceId)) {
      return;
    }
    this.expandedSourceRowId.update((cur) => (cur === row.id ? null : row.id));
  }

  toggleJsonDetail(row: SourceTransactionsView): void {
    if (!this.findLinkedTransaction(row.sourceId)) {
      return;
    }
    this.jsonExpandedSourceRowId.update((cur) => (cur === row.id ? null : row.id));
  }

  toggleJsonPanelCategories(): void {
    this.jsonPanelShowCategories.update((v) => !v);
  }

  isExpanded(rowId: string): boolean {
    return this.expandedSourceRowId() === rowId;
  }

  isJsonExpanded(rowId: string): boolean {
    return this.jsonExpandedSourceRowId() === rowId;
  }

  /** Vault transaction to show under a source row when expanded and a link exists. */
  linkedTxForExpandedRow(row: SourceTransactionsView): TransactionView | undefined {
    if (!this.isExpanded(row.id)) {
      return undefined;
    }
    return this.findLinkedTransaction(row.sourceId);
  }

  directionLabel(dir: TransactionDirection): string {
    if (dir === TransactionDirection.In) {
      return "In";
    }
    if (dir === TransactionDirection.Out) {
      return "Out";
    }
    return String(dir ?? "");
  }

  categoriesLabel(row: SourceTransactionsView): string {
    return row.categories?.length ? row.categories.join(", ") : "—";
  }

  txCategoriesLabel(tx: TransactionView): string {
    return tx.c_categories?.length ? tx.c_categories.join(", ") : "—";
  }

  txQuantityAmount(tx: TransactionView): string {
    try {
      return String(tx.quantity?.actualQuantity?.amount ?? "");
    } catch {
      return "—";
    }
  }

  txCurrency(tx: TransactionView): string {
    return tx.quantity?.currency ?? tx.quantity?.actualQuantity?.symbol ?? "—";
  }

  formatTxPostDate(tx: TransactionView): string {
    if (!tx.postDate) {
      return "—";
    }
    return tx.postDate.toLocaleDateString();
  }

  originLabel(ori: TransactionView["origin"]): string {
    return ori != null ? String(ori) : "—";
  }

  categoriesJsonForTransaction(tx: TransactionView): string {
    const allCats = this.userStore.categories.categoryViews() ?? [];
    const idSet = new Set<string>();
    for (const a of tx.allocations ?? []) {
      const id = a.categoryLink?.id;
      if (id) {
        idSet.add(id);
      }
    }
    const categories: CategoryView[] = [];
    const unresolvedCategoryIds: string[] = [];
    for (const id of idSet) {
      const cv = allCats.find((c) => c.id === id);
      if (cv) {
        categories.push(cv);
      } else {
        unresolvedCategoryIds.push(id);
      }
    }
    const payload: {
      categories: CategoryView[];
      unresolvedCategoryIds?: string[];
    } = { categories };
    if (unresolvedCategoryIds.length) {
      payload.unresolvedCategoryIds = unresolvedCategoryIds;
    }
    try {
      return this.serializeValueForJson(payload);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return `Unable to serialize categories: ${msg}`;
    }
  }

  /** Pretty-printed JSON for a linked vault `TransactionView` (dates as ISO; functions omitted). */
  transactionViewAsJson(tx: TransactionView): string {
    try {
      return this.serializeValueForJson(tx);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return `Unable to serialize TransactionView: ${msg}`;
    }
  }

  private serializeValueForJson(value: unknown): string {
    return JSON.stringify(
      value,
      (key, v) => {
        if (typeof v === "function") {
          return undefined;
        }
        if (v instanceof Date) {
          return v.toISOString();
        }
        return v;
      },
      2,
    );
  }

  sourceRawJsonPretty(row: SourceTransactionsView): string {
    const raw = row.raw?.trim();
    if (!raw) {
      return "—";
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return `/* Not valid JSON — showing raw string */\n${raw}`;
    }
  }

  readonly tableColCount = 9;

  /** ── Vault Explorer ── */

  readonly VaultCodeEnum = VaultCodeEnum;
  readonly vaultLoading = this.vaultExplorer.loading;
  readonly vaultLoadingType = this.vaultExplorer.loadingType;
  readonly vaultTypes = this.vaultExplorer.availableTypes;
  readonly vaultTypeCounts = this.vaultExplorer.vaultTypeCounts;
  readonly selectedVaultType = signal<VaultCodeEnum | null>(null);

  onVaultTypeSelected(label: string): void {
    const types = this.vaultTypes();
    const found = types.find((code) => this.vaultExplorer.typeLabel(code) === label);
    this.selectedVaultType.set(found ?? null);
    if (found) {
      this.vaultExplorer.loadVaultDataForType(found);
    }
  }
}
